// src/components/Orders/TicketCard.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";

import { getElapsedTime, getCardColor, getTicketFiredTime } from "./utils.js";
import TicketItem from "./TicketItem.js";
import { logClientActivity } from "../../utils/clientLogger";
import { isCompatibilityMouseEvent } from "./touchInput";

export default function TicketCard({
  order,
  orderIndex,
  totalCards = 1,

  onItemTap,
  onItemDoubleTap,
  onBump,
  onPrepItemReady = () => { },

  onBumpCourse = () => { },
  onCourseComplete = () => { },

  settings = {},
  isPrepScreen = false,

  isPeekMode = false,
  onPeekSelect = null,

  isFireCourseMode = false,
  onSelectFireCourse = () => { },
  setIsFireCourseMode = () => { },
  isFireItemMode = false,
  onSelectFireItem = () => { },
  isPrintCourseMode = false,
  onSelectPrintCourse = () => { },

  isFireTicketMode = false,
  isPrintTicketMode = false,

  setPeekedOrder = () => { },
  setPeekModeIntent = () => { },
  dragHandleMode = "card",
  dragHandleClassName = "drag-handle",
  onDragHandleTouchStart = null,
  dragHandleProps = {},
  rootClassName = "",
  dataTicketKey = null,
  disableBottomCutoffFade = false,
  disableModeBlur = false,
  isBumpBarTicketSelected = false,
  isBumpBarItemMode = false,
  bumpBarSelectedItemId = null,
}) {
  // Local UI state
  const [timerTick, setTimerTick] = useState(0);
  const [showBottomCutoffFade, setShowBottomCutoffFade] = useState(false);
  const [bottomCutoffFadeStyle, setBottomCutoffFadeStyle] = useState(null);
  const [hiddenPrepCoursePrompt, setHiddenPrepCoursePrompt] = useState(null);
  const cardRef = useRef(null);
  const ticketTouchStateRef = useRef({ suppressUntil: 0 });

  const suppressItemCompatibilityEvent = (event) => {
    if (!isCompatibilityMouseEvent(event, ticketTouchStateRef.current)) return;
    // Only item touches arm this guard; unrelated header touch clicks retain
    // their existing behavior when no item gesture has just completed.
    if (Date.now() >= ticketTouchStateRef.current.suppressUntil) return;
    const native = event.nativeEvent || event;
    logClientActivity("kds.ticket_input_suppressed", {
      ticket_instance_id: order.ticket_instance_id,
      order_id: order.order_id ?? null,
      event_type: event.type,
      event_timestamp_ms: event.timeStamp,
      fires_touch_events: native.sourceCapabilities?.firesTouchEvents ?? null,
      decision: "suppressed_item_compatibility_event",
    });
    event.preventDefault();
    event.stopPropagation();
  };

  // Double tap detection (header)
  const lastTapRef = useRef(0);

  // UI scale map
  const UI_SCALE_CONFIG = {
    small: {
      orderNumber: "text-xs",
      headerLine: "text-xs",
      messageArea: "text-[10px]",
      courseSeparator: "text-[10px]",
      timers: "text-xs",
      categoryHeader: "text-sm",
      advancedtimers: "text-[10px]",
      bumpbutton: "text-[11px]",
    },
    medium: {
      orderNumber: "text-md",
      headerLine: "text-md",
      messageArea: "text-sm",
      courseSeparator: "text-sm",
      timers: "text-md",
      categoryHeader: "text-xl",
      advancedtimers: "text-sm",
      bumpbutton: "text-sm",
    },
    large: {
      orderNumber: "text-xl",
      headerLine: "text-lg",
      messageArea: "text-base",
      courseSeparator: "text-base",
      timers: "text-xl",
      categoryHeader: "text-2xl",
      advancedtimers: "text-base",
      bumpbutton: "text-xl",
    },
    extralarge: {
      orderNumber: "text-3xl",
      headerLine: "text-2xl",
      messageArea: "text-xl",
      courseSeparator: "text-xl",
      timers: "text-2xl",
      categoryHeader: "text-4xl",
      advancedtimers: "text-xl",
      bumpbutton: "text-3xl",
    },
  };

  const uiScaleKey = settings?.uiscale || "medium";
  const uiScale = UI_SCALE_CONFIG[uiScaleKey] || UI_SCALE_CONFIG.medium;
  const itemPrepDisplayMode = ["normal", "darkened", "hidden"].includes(
    String(settings?.item_prep_display_mode || "").toLowerCase()
  )
    ? String(settings.item_prep_display_mode).toLowerCase()
    : "normal";

  // Force repaint timers
  useEffect(() => {
    const interval = setInterval(() => setTimerTick((v) => v + 1), 1000);
    return () => clearInterval(interval);
  }, []);

  // Core data
  const items = order.items || [];
  const summaryItemCount = useMemo(() => {
    return items.filter((item) => {
      if (!item) return false;
      if (item.isParked || String(item.status || "").toLowerCase() === "parked") return false;
      if (item.isCourseSeparator || item.isMessage || item.isFireItem) return false;
      return true;
    }).length;
  }, [items]);
  const createdAt = useMemo(() => {
    const itemStartMs = items
      .map((i) => new Date(i?.createdAt).getTime())
      .filter((ms) => Number.isFinite(ms));

    const baseMs = itemStartMs.length
      ? Math.min(...itemStartMs)
      : new Date(order.createdAt).getTime();

    return new Date(Number.isFinite(baseMs) ? baseMs : Date.now());
  }, [items, order.createdAt]);

  // Visible items for rendering (coursing + separators)
  const visibleItemSettings = useMemo(() => {
    if (!isBumpBarItemMode) return settings;
    return {
      ...settings,
      hide_bumped_courses: false,
    };
  }, [isBumpBarItemMode, settings]);

  const visibleItems = useMemo(() => {
    return getVisibleItems(items, visibleItemSettings);
  }, [items, visibleItemSettings, itemPrepDisplayMode, timerTick]);

  useEffect(() => {
    if (!isBumpBarTicketSelected) return;
    cardRef.current?.scrollIntoView({
      block: "nearest",
      inline: "nearest",
      behavior: "smooth",
    });
  }, [isBumpBarTicketSelected]);

  useEffect(() => {
    if (disableBottomCutoffFade) {
      setShowBottomCutoffFade(false);
      setBottomCutoffFadeStyle(null);
      return undefined;
    }

    const cardEl = cardRef.current;
    const scrollHost = document.getElementById("main-content");
    if (!cardEl || !scrollHost) return undefined;

    let frameId = null;

    const updateFadeVisibility = () => {
      frameId = null;
      const nextCardEl = cardRef.current;
      const nextScrollHost = document.getElementById("main-content");
      if (!nextCardEl || !nextScrollHost) {
        setShowBottomCutoffFade(false);
        setBottomCutoffFadeStyle(null);
        return;
      }

      const cardRect = nextCardEl.getBoundingClientRect();
      const hostRect = nextScrollHost.getBoundingClientRect();

      const isVisibleAtAll =
        cardRect.bottom > hostRect.top + 12 &&
        cardRect.top < hostRect.bottom - 12;
      const extendsBelowVisibleArea = cardRect.bottom > hostRect.bottom + 8;
      const shouldShow = isVisibleAtAll && extendsBelowVisibleArea;

      setShowBottomCutoffFade(shouldShow);
      if (shouldShow) {
        setBottomCutoffFadeStyle({
          left: Math.round(cardRect.left),
          width: Math.round(cardRect.width),
        });
      } else {
        setBottomCutoffFadeStyle(null);
      }
    };

    const scheduleUpdate = () => {
      if (frameId != null) return;
      frameId = window.requestAnimationFrame(updateFadeVisibility);
    };

    scheduleUpdate();

    scrollHost.addEventListener("scroll", scheduleUpdate, { passive: true });
    window.addEventListener("resize", scheduleUpdate);

    let resizeObserver = null;
    if (typeof ResizeObserver !== "undefined") {
      resizeObserver = new ResizeObserver(() => scheduleUpdate());
      resizeObserver.observe(cardEl);
      resizeObserver.observe(scrollHost);
    }

    return () => {
      scrollHost.removeEventListener("scroll", scheduleUpdate);
      window.removeEventListener("resize", scheduleUpdate);
      if (resizeObserver) resizeObserver.disconnect();
      if (frameId != null) {
        window.cancelAnimationFrame(frameId);
      }
    };
  }, [disableBottomCutoffFade, order.ticket_instance_id, items.length, visibleItems.length]);

  // Helpers: visible items logic
  function getVisibleItems(allItems, s) {
    const applyPrepVisibility = (list) => {
      if (itemPrepDisplayMode !== "hidden") return list;
      const visibleRealCourses = new Set(
        (list || [])
          .filter((item) => !item?.isCourseSeparator && !item?.isMessage && !item?.isFireItem && !isRealPrepDisplayItem(item))
          .map((item) => item?.course)
      );
      return (list || []).filter((item) => {
        if (isRealPrepDisplayItem(item)) return false;
        if (item?.isCourseSeparator) return visibleRealCourses.has(item?.course);
        return true;
      });
    };

    if (!s?.coursing) return applyPrepVisibility(allItems);

    const fireItems = allItems.filter((i) => i.isFireItem);

    if (s.use_course_separator) {
      return applyPrepVisibility([...fireItems, ...filterVisibleCoursesBySeparator(allItems, s)]);
    }

    return applyPrepVisibility([...fireItems, ...filterVisibleCoursesByCategory(allItems, s)]);
  }

  function isVoidedItem(item) {
    const status = String(item?.status || "").toLowerCase();
    return !!item?.isVoided || Number(item?.TransType) === 2 || status === "voided";
  }

  function isVisibleAddonItem(item) {
    const isAddon =
      item?.isAddon === true ||
      item?.is_addon === true ||
      item?.is_addon === 1 ||
      item?.is_addon === "1" ||
      item?.isaddon === true ||
      item?.isaddon === 1 ||
      item?.isaddon === "1";
    return isAddon && item?.suppressAddonDisplay !== true;
  }

  function getCookSeconds(item) {
    const cookSeconds = Number(item?.cookTimeSeconds);
    if (Number.isFinite(cookSeconds) && cookSeconds > 0) return Math.round(cookSeconds);
    const minutes = Number(item?.preparationTime ?? item?.PreparationTime ?? 0);
    return Number.isFinite(minutes) && minutes > 0 ? Math.round(minutes * 60) : null;
  }

  function isCookTimingCandidate(item) {
    if (!item || item.isCourseSeparator || item.isMessage || item.isFireItem) return false;
    if (item.isBumped || item.isParked || isVoidedItem(item)) return false;
    const status = String(item.status || "").toLowerCase();
    if (status === "bumped" || status === "parked") return false;
    if (item.prepReadyAt || item.cookTimingStartedManually) return false;
    if (isVisibleAddonItem(item)) return false;
    return getCookSeconds(item) != null;
  }

  function getItemStartMs(item) {
    const itemStartMs = item?.createdAt ? new Date(item.createdAt).getTime() : NaN;
    return Number.isFinite(itemStartMs) ? itemStartMs : createdAt.getTime();
  }

  function getCookStartDelaySeconds(item) {
    if (!isCookTimingCandidate(item)) return 0;
    const itemCookSeconds = getCookSeconds(item);
    if (itemCookSeconds == null) return 0;
    const course = Number.isFinite(Number(item?.course)) ? Number(item.course) : 0;
    const courseItems = (items || []).filter(
      (candidate) =>
        Number(candidate?.course ?? 0) === course &&
        isCookTimingCandidate(candidate)
    );
    const maxCookSeconds = courseItems.reduce((max, candidate) => {
      const cookSeconds = getCookSeconds(candidate);
      return cookSeconds == null ? max : Math.max(max, cookSeconds);
    }, 0);
    return Math.max(0, maxCookSeconds - itemCookSeconds);
  }

  function getSignedCookStartSeconds(item) {
    const startDelaySeconds = getCookStartDelaySeconds(item);
    if (!startDelaySeconds) return 0;
    const startMs = getItemStartMs(item);
    if (!Number.isFinite(startMs)) return 0;
    const endAt = item?.isParked && item?.parkedAt ? new Date(item.parkedAt) : new Date();
    const elapsedSeconds = Math.max(
      0,
      Math.floor((endAt.getTime() - startMs - (Number(item?.totalPausedSeconds) || 0) * 1000) / 1000)
    );
    return elapsedSeconds - startDelaySeconds;
  }

  function isRealPrepDisplayItem(item) {
    if (!item || item.isCourseSeparator || item.isMessage || item.isFireItem) return false;
    if (item.isBumped || isVoidedItem(item)) return false;
    const status = String(item.status || "").toLowerCase();
    if (status === "voided" || status === "bumped" || status === "parked") return false;
    return getSignedCookStartSeconds(item) < 0;
  }

  function shouldDarkenPrepItem(item) {
    return itemPrepDisplayMode === "darkened" && isRealPrepDisplayItem(item);
  }

  function filterVisibleCoursesByCategory(allItems, s) {
    const grouped = new Map(); // courseIndex -> items[]
    for (const item of allItems) {
      if (!grouped.has(item.course)) grouped.set(item.course, []);
      grouped.get(item.course).push(item);
    }

    const visible = [];
    const courseIndices = [...grouped.keys()].sort((a, b) => a - b);

    for (let i = 0; i < courseIndices.length; i++) {
      const course = courseIndices[i];
      const courseItems = grouped.get(course);

      const realItems = courseItems.filter((x) => !x.isCourseSeparator);
      const allBumped = realItems.length > 0 && realItems.every((x) => x.isBumped);

      const prevCoursesBumped = courseIndices.slice(0, i).every((prev) => {
        const prevItems = grouped.get(prev) || [];
        return prevItems.every((x) => x.isBumped || x.isCourseSeparator);
      });

      if (s.hide_bumped_courses && allBumped) continue;
      if (s.hide_course && !prevCoursesBumped) continue;

      visible.push(...courseItems);
    }

    return visible;
  }

  function filterVisibleCoursesBySeparator(allItems, s) {
    const grouped = new Map(); // courseIndex -> items[]
    for (const item of allItems) {
      const course = item.course;
      if (!grouped.has(course)) grouped.set(course, []);
      grouped.get(course).push(item);
    }

    const visible = [];
    const courseIndices = [...grouped.keys()].sort((a, b) => a - b);

    for (let i = 0; i < courseIndices.length; i++) {
      const course = courseIndices[i];
      const courseItems = grouped.get(course);

      const realItems = courseItems.filter((x) => !x.isCourseSeparator);
      const allBumped = realItems.length > 0 && realItems.every((x) => x.isBumped);

      const prevCoursesBumped = courseIndices.slice(0, i).every((prev) => {
        const prevItems = grouped.get(prev) || [];
        const prevReal = prevItems.filter((x) => !x.isCourseSeparator);
        return prevReal.length === 0 ? true : prevReal.every((x) => x.isBumped);
      });

      const unbumpedReal = realItems.filter((x) => !x.isBumped);
      const onlySeparatorLeft = unbumpedReal.length === 0;

      if (s.hide_bumped_courses && allBumped) continue;
      if (s.hide_course && (!prevCoursesBumped || onlySeparatorLeft)) continue;

      visible.push(...courseItems);
    }

    return visible;
  }

  // Computed: course indices display (current + total)
  const courseIndices = useMemo(() => {
    const realItems = (items || []).filter(
      (item) =>
        item &&
        !item.isCourseSeparator &&
        !item.isMessage &&
        !item.isFireItem &&
        item.course !== undefined &&
        item.course !== null
    );

    const sortedAll = [...new Set(realItems.map((item) => Number(item.course)).filter(Number.isFinite))]
      .sort((a, b) => a - b);
    const sortedUnbumped = [
      ...new Set(
        realItems
          .filter((item) => !item.isBumped)
          .map((item) => Number(item.course))
          .filter(Number.isFinite)
      ),
    ].sort((a, b) => a - b);

    const currentRaw =
      sortedUnbumped[0] !== undefined
        ? sortedUnbumped[0]
        : sortedAll.length > 0
          ? sortedAll[sortedAll.length - 1]
          : null;
    const currentIndex =
      currentRaw != null ? sortedAll.findIndex((course) => course === currentRaw) : -1;

    return {
      total: sortedAll.length,
      current: currentIndex >= 0 ? currentIndex + 1 : 0,
      currentRaw,
    };
  }, [items]);

  const currentCourseLabel = useMemo(() => {
    if (
      !settings?.coursing ||
      settings?.use_course_separator ||
      settings?.display_course_labels === false
    ) {
      return null;
    }
    const currentCourseRaw = Number(courseIndices?.currentRaw);
    if (!Number.isFinite(currentCourseRaw) || currentCourseRaw < 0) return null;
    const label = settings?.course_labels?.[String(currentCourseRaw + 1)];
    const normalized = String(label || "").trim();
    return normalized || `Course ${currentCourseRaw + 1}`;
  }, [
    courseIndices?.currentRaw,
    settings?.coursing,
    settings?.display_course_labels,
    settings?.use_course_separator,
    settings?.course_labels,
  ]);

  // Fire timer
  const firstFiredAt = useMemo(() => getTicketFiredTime(items), [items]);
  const firedTimer = firstFiredAt
    ? getElapsedTime(firstFiredAt, {
      isParked: order.isParked,
      parkedAt: order.parkedAt,
      totalPausedSeconds: order.totalPausedSeconds,
    })
    : null;

  const hasAnyUnbumpedRealItems = useMemo(() => {
    return (items || []).some(
      (i) => !i.isCourseSeparator && !i.isMessage && !i.isBumped
    );
  }, [items]);

  const showTicketBumpDisplayBoxes = useMemo(() => {
    return (
      settings?.display_bump_status_of_other_units === true &&
      settings?.display_bump_status_other_units_ticket === true &&
      Array.isArray(order?.ticket_bump_display_boxes) &&
      order.ticket_bump_display_boxes.length > 0
    );
  }, [
    order?.ticket_bump_display_boxes,
    settings?.display_bump_status_of_other_units,
    settings?.display_bump_status_other_units_ticket,
  ]);

  const showCourseBumpDisplayBoxes =
    settings?.display_bump_status_of_other_units === true &&
    settings?.display_bump_status_other_units_course === true;

  const bumpCourseItems = (courseToBump, courseItems, unbumpedCourseCount) => {
    // Only tap items that are not bumped (prevents local double toggles)
    courseItems
      .filter((i) => !i.isBumped)
      .forEach((item) => onItemTap?.(order.ticket_instance_id, item.ID));

    onBumpCourse(order.ticket_instance_id, courseToBump, courseItems);

    if (unbumpedCourseCount === 1) {
      onCourseComplete(order.ticket_instance_id);
    }
  };

  const bumpSelectedCourseItems = (courseToBump, selectedItems) => {
    const bumpableItems = (selectedItems || []).filter((item) => !item?.isBumped);
    if (!bumpableItems.length) return;
    bumpableItems.forEach((item) => onItemTap?.(order.ticket_instance_id, item.ID));
  };

  const getUnbumpedCourseCountAfterItemBump = (itemBeingBumped) => {
    const courseSet = new Set();
    (items || []).forEach((item) => {
      if (!item || item.isCourseSeparator || item.isMessage || item.isFireItem) return;
      if (String(item.ID) === String(itemBeingBumped?.ID)) return;
      if (item.isBumped) return;
      if (item.course === undefined || item.course === null) return;
      courseSet.add(item.course);
    });
    return courseSet.size;
  };

  const maybePromptHiddenPrepAfterItemBump = (itemBeingBumped) => {
    if (itemPrepDisplayMode !== "hidden") return false;
    if (!itemBeingBumped || itemBeingBumped.isBumped) return false;
    if (isRealPrepDisplayItem(itemBeingBumped)) return false;

    const courseToCheck = itemBeingBumped.course;
    const courseItems = items.filter(
      (item) =>
        item?.course === courseToCheck &&
        !item?.isCourseSeparator &&
        !item?.isMessage &&
        !item?.isFireItem
    );
    const hiddenPrepItems = courseItems.filter(
      (item) => !item.isBumped && isRealPrepDisplayItem(item)
    );
    if (!hiddenPrepItems.length) return false;

    const unbumpedReadyItems = courseItems.filter(
      (item) => !item.isBumped && !isRealPrepDisplayItem(item)
    );
    const isLastReadyItem =
      unbumpedReadyItems.length === 1 &&
      String(unbumpedReadyItems[0]?.ID) === String(itemBeingBumped.ID);
    if (!isLastReadyItem) return false;

    setHiddenPrepCoursePrompt({
      courseToBump: courseToCheck,
      courseItems: hiddenPrepItems,
      hiddenPrepItems,
      readyItemsToBump: [],
      unbumpedCourseCount: getUnbumpedCourseCountAfterItemBump(itemBeingBumped),
    });
    return true;
  };

  // Bump-next-course logic (always bumps the next unbumped course when coursing is enabled)
  const handleBumpNextCourse = () => {
    const unbumpedCourses = [
      ...new Set(
        items
          .filter((i) => !i.isBumped && !i.isCourseSeparator && !i.isMessage)
          .map((i) => i.course)
          .filter((c) => c !== undefined && c !== null)
      ),
    ].sort((a, b) => a - b);

    const courseToBump = unbumpedCourses[0];

    // ? If nothing left to bump in coursing, bump the whole ticket instead
    if (courseToBump === undefined) {
      onBump?.(order);
      return;
    }

    const courseItems = items.filter(
      (i) => i.course === courseToBump && !i.isCourseSeparator
    );
    const hiddenPrepItems = itemPrepDisplayMode === "hidden"
      ? courseItems.filter((item) => !item.isBumped && isRealPrepDisplayItem(item))
      : [];

    if (hiddenPrepItems.length > 0) {
      setHiddenPrepCoursePrompt({
        courseToBump,
        courseItems,
        hiddenPrepItems,
        readyItemsToBump: courseItems.filter(
          (item) => !item.isBumped && !hiddenPrepItems.some((hidden) => String(hidden.ID) === String(item.ID))
        ),
        unbumpedCourseCount: unbumpedCourses.length,
      });
      return;
    }

    bumpCourseItems(courseToBump, courseItems, unbumpedCourses.length);
  };

  const handleShowHiddenPrepItems = () => {
    const prompt = hiddenPrepCoursePrompt;
    setHiddenPrepCoursePrompt(null);
    prompt?.hiddenPrepItems?.forEach((item) => {
      onPrepItemReady(order.ticket_instance_id, item.ID);
    });
    bumpSelectedCourseItems(prompt?.courseToBump, prompt?.readyItemsToBump);
  };

  const handleBumpDespiteHiddenPrepItems = () => {
    const prompt = hiddenPrepCoursePrompt;
    setHiddenPrepCoursePrompt(null);
    if (!prompt) return;
    bumpCourseItems(prompt.courseToBump, prompt.courseItems, prompt.unbumpedCourseCount);
  };

  const logHeaderInput = (event, decision, extra = {}) => {
    const native = event?.nativeEvent || event;
    const touch = event?.changedTouches?.[0] || event?.touches?.[0];
    logClientActivity("kds.ticket_header_input", {
      ticket_instance_id: order.ticket_instance_id,
      order_id: order.order_id ?? null,
      event_type: event?.type ?? null,
      event_timestamp_ms: event?.timeStamp ?? null,
      client_time_ms: Date.now(),
      is_trusted: native?.isTrusted ?? null,
      click_count: event?.detail ?? null,
      fires_touch_events: native?.sourceCapabilities?.firesTouchEvents ?? null,
      touch_count: event?.touches?.length ?? null,
      touch_id: touch?.identifier ?? null,
      client_x: touch?.clientX ?? event?.clientX ?? null,
      client_y: touch?.clientY ?? event?.clientY ?? null,
      coursing: !!settings?.coursing,
      is_peek_mode: !!isPeekMode,
      decision,
      ...extra,
    });
  };

  // Double tap on header
  const handleDoubleTapHeader = (event) => {
    const now = Date.now();
    const delta = now - lastTapRef.current;
    logHeaderInput(event, delta < 300
      ? (settings?.coursing ? "invoke_next_course_bump" : "invoke_ticket_bump")
      : "no_double_tap", {
      since_last_click_ms: lastTapRef.current ? delta : null,
    });

    if (delta < 300) {
      if (settings?.coursing) {
        // Will bump next course, OR bump ticket if nothing is unbumped
        handleBumpNextCourse();
      } else {
        onBump?.(order);
      }
    }

    lastTapRef.current = now;
  };

  // Detailed timing header for a course
  function renderCourseTimingHeader(courseIndex, courseItems) {
    if (!settings?.show_detailed_timing_data) return null;

    const realItems = courseItems.filter((i) => !i.isCourseSeparator);
    const firedItems = realItems.filter((i) => i.isFired && i.firedAt);

    const firedAt = firedItems.length
      ? new Date(Math.min(...firedItems.map((i) => new Date(i.firedAt).getTime())))
      : null;

    const allBumped =
      realItems.length > 0 && realItems.every((i) => i.isBumped && i.bumpedAt);

    const bumpedAt = allBumped
      ? new Date(Math.max(...realItems.map((i) => new Date(i.bumpedAt).getTime())))
      : null;

    if (!firedAt && !bumpedAt) return null;

    const now = new Date();
    const firedAtStr = firedAt
      ? firedAt.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
      : "--";
    const bumpedAtStr = bumpedAt
      ? bumpedAt.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
      : "--";

    const firedTimerLocal = firedAt ? getElapsedTime(firedAt, bumpedAt || now) : "--:--";
    const bumpedTimerLocal = bumpedAt ? getElapsedTime(bumpedAt) : "--:--";

    return (
      <div key={`course-timing-${courseIndex}`} className="flex flex-col items-center">
        {firedAt && (
          <div className={`${uiScale.advancedtimers} font-medium text-gray-500 dark:text-white`}>
            Fired: {firedAtStr} TIMER: {firedTimerLocal}
          </div>
        )}
        {bumpedAt && (
          <div className={`${uiScale.advancedtimers} text-gray-500 dark:text-white`}>
            Bumped: {bumpedAtStr} TIMER: {bumpedTimerLocal}
          </div>
        )}
      </div>
    );
  }

  function renderCourseSeparator(item) {
    const courseLabel = item.course !== undefined ? `Course ${item.course + 1}` : "Course";
    return (
      <div
        key={`separator-${item.ID}`}
        className="text-center font-bold text-md text-gray-600 dark:text-gray-400 uppercase tracking-wide py-1"
      >
        ------- {courseLabel} ------
      </div>
    );
  }

  function renderDisplayBoxes(boxes, keyPrefix) {
    if (!Array.isArray(boxes) || !boxes.length) return null;
    return (
      <div className="flex items-center gap-2">
        {boxes.map((box) => {
          const isReceived = !!box?.received;
          const isBumped = !!box?.bumped;
          const isSelf = !!box?.is_self;

          return (
            <div
              key={`${keyPrefix}-${box.station_id}`}
              className={`relative h-4 w-4 rounded-sm border transition-all duration-200 ${
                isReceived ? "opacity-100" : "opacity-0"
              } ${
                isBumped
                  ? "bg-gray-200 border-gray-400 dark:bg-slate-800 dark:border-slate-500"
                  : isSelf
                    ? "bg-transparent border-yellow-500 dark:border-yellow-400"
                    : "bg-white/70 border-black/20 dark:border-white/20 dark:bg-gray-900/60"
              }`}
              aria-hidden={!isReceived}
            >
              {isReceived && isBumped && (
                <>
                  <div className="absolute left-[-2px] right-[-2px] top-1/2 -translate-y-1/2 rotate-45 border-t-2 border-black/60 dark:border-white/60" />
                  <div className="absolute left-[-2px] right-[-2px] top-1/2 -translate-y-1/2 -rotate-45 border-t-2 border-black/60 dark:border-white/60" />
                </>
              )}
            </div>
          );
        })}
      </div>
    );
  }

  function renderCourseHeaderPanel(content, key) {
    if (!content) return null;
    return (
      <div
        key={key}
        className="mb-2 rounded-md border border-black/10 bg-white/60 p-2 dark:border-white/10 dark:bg-gray-800/50"
      >
        {content}
      </div>
    );
  }

  function renderCombinedCourseHeaderPanel(courseIndex, courseBoxes, courseItems, keyPrefix) {
    const courseLabel =
      settings?.display_course_labels === false
        ? ""
        : String(
            settings?.course_labels?.[String(Number(courseIndex) + 1)] ||
              `Course ${Number(courseIndex) + 1}`
          ).trim();
    const boxesContent = showCourseBumpDisplayBoxes
      ? renderDisplayBoxes(
          courseBoxes,
          `course-box-${order.ticket_instance_id}-${courseIndex}`
        )
      : null;
    const timingContent = renderCourseTimingHeader(courseIndex, courseItems);

    if (!courseLabel && !boxesContent && !timingContent) return null;

    return renderCourseHeaderPanel(
      <div className="space-y-2">
        {settings?.coursing && courseLabel && (
          <div className="text-center text-sm font-semibold uppercase tracking-wide text-purple-700 dark:text-purple-200">
            {courseLabel}
          </div>
        )}
        {boxesContent}
        {timingContent}
      </div>,
      keyPrefix
    );
  }

  // Render
  return (
    <div
      ref={cardRef}
      data-ticket-key={dataTicketKey || undefined}
      className={`relative rounded shadow flex flex-col h-full transition-colors duration-500 ease-in-out box-border ${rootClassName} ${dragHandleMode === "card" ? dragHandleClassName : ""}
        ${getCardColor(createdAt, items, settings)}
        ${((isPeekMode && !isFireItemMode && !isFireCourseMode && !isPrintCourseMode) || isFireTicketMode || isPrintTicketMode || isBumpBarTicketSelected)
          ? "border-4 border-yellow-400 shadow-yellow-300/30"
          : "border border-white/20"
        }`}
      style={{
        flex: "0 0 auto",
        minWidth: 0,
        maxWidth: "100%",
        width: "100%",
        userSelect: "none",
        WebkitUserSelect: "none",
        MozUserSelect: "none",
      }}
      {...(dragHandleMode === "card" ? dragHandleProps : {})}
      onClickCapture={suppressItemCompatibilityEvent}
      onDoubleClickCapture={suppressItemCompatibilityEvent}
      onTouchStartCapture={() => {
        // A fresh touch is a new gesture, not leftover input from the old row.
        ticketTouchStateRef.current.suppressUntil = 0;
      }}
      onClick={() => {
        if (isPeekMode || isFireTicketMode || isPrintTicketMode) onPeekSelect?.(order);
      }}
    >
      <div className="relative px-2 py-1.5 flex-1">
        {!disableModeBlur && (isFireCourseMode || isPrintCourseMode) && (
          <div className="absolute inset-0 rounded backdrop-blur-sm z-0" aria-hidden="true" />
        )}

        {order.isRush && (
          <div className="text-red-600 font-bold text-xs uppercase mb-1 text-center animate-pulse">
            ?? RUSH ORDER
          </div>
        )}

        <div
          className={`kds-ticket-header ${dragHandleMode === "header" ? dragHandleClassName : ""}`}
          {...(dragHandleMode === "header" ? dragHandleProps : {})}
          onTouchStartCapture={(event) => {
            logHeaderInput(event, "observed");
            onDragHandleTouchStart?.();
          }}
          onTouchEndCapture={(event) => logHeaderInput(event, "observed")}
          onTouchCancelCapture={(event) => logHeaderInput(event, "observed")}
          onDoubleClick={(event) => logHeaderInput(event, "observed")}
          onClick={(event) => {
            if (!isPeekMode) handleDoubleTapHeader(event);
            else logHeaderInput(event, "ignored_peek_mode");
          }}
        >
          {totalCards > 1 && order.card && (
            <div className="text-l text-gray-400 text-center mb-1">
              Card {order.card} of {totalCards}
            </div>
          )}

          <div className="flex justify-between items-center text-sm font-bold mb-1">
            <div className="flex items-center gap-1">
              <span className={`${uiScale.orderNumber} font-bold`}>#{order.order_id}</span>
            </div>

            <div className={`${uiScale.timers} flex flex-col items-end`}>
              <span>
                {getElapsedTime(createdAt, {
                  isParked: order.isParked,
                  parkedAt: order.parkedAt,
                  totalPausedSeconds: order.totalPausedSeconds,
                })}
              </span>

              {settings.enable_fire_timer && <span className="text-red-500">{firedTimer}</span>}
            </div>
          </div>

          <div className={`ticket-meta-line mb-1 ${uiScale.headerLine}`}>
            {order.table_name} - {order.server_name}
          </div>

          <div className={`ticket-meta-line mb-1 ${uiScale.headerLine}`}>
            {order.destination} - {summaryItemCount} Total Items
          </div>

          <div className={`ticket-meta-line mb-1 ${uiScale.headerLine}`}>{order.user_info}</div>
          <div className={`ticket-meta-line mb-1 ${uiScale.headerLine}`}>{order.covers}</div>

          {settings.coursing && (
          <div className={`ticket-meta-line mb-1 ${uiScale.headerLine}`}>
            {currentCourseLabel
              ? `${currentCourseLabel} (${courseIndices.current} of ${courseIndices.total})`
                : `Course ${courseIndices.current} of ${courseIndices.total}`}
            </div>
          )}
        </div>

        {order.order_messages?.length > 0 && (
          <div
            className={`p-1.5 mb-4 rounded border ${uiScale.messageArea} transition-all duration-200 cursor-pointer bg-white text-black dark:bg-gray-900 dark:text-gray-100`}
          >
            {order.order_messages.map((msg, i) => (
              <div key={i}>{msg}</div>
            ))}
          </div>
        )}

        {showTicketBumpDisplayBoxes && (
          <div
            className={`${order.order_messages?.length > 0 ? "mt-0" : "mt-1"} mb-2 rounded-md border border-black/10 dark:border-white/10 bg-white/60 dark:bg-gray-800/50 p-1.5`}
          >
            {renderDisplayBoxes(
              order.ticket_bump_display_boxes,
              `ticket-box-${order.ticket_instance_id}`
            )}
          </div>
        )}
      </div>

      <ul className={`space-y-2 ${uiScale.courseSeparator} relative z-10 px-2 pt-1.5`}>
        {(() => {
          let lastCategory = null;

          const filtered = visibleItems.filter((i) => !i.isMessage);
          const groupedByCourse = new Map();

          for (const item of filtered) {
            if (!groupedByCourse.has(item.course)) groupedByCourse.set(item.course, []);
            groupedByCourse.get(item.course).push(item);
          }

          return [...groupedByCourse.entries()].map(([courseIndex, courseItems]) => {
            const highlightClass = (isFireCourseMode || isPrintCourseMode)
              ? "border-4 border-yellow-400 rounded-md bg-yellow-50 dark:bg-yellow-900/10 p-1"
              : "";
            const courseBoxes =
              order?.course_bump_display_boxes?.[String(courseIndex)] ||
              order?.course_bump_display_boxes?.[courseIndex] ||
              [];
            const hasSeparator = courseItems.some((item) => item?.isCourseSeparator);

            return (
              <li
                key={`course-${courseIndex}`}
                className={`${highlightClass} space-y-2`}
                onClick={(e) => {
                  e.stopPropagation();

                  if (isFireCourseMode && onSelectFireCourse) {
                    onSelectFireCourse({
                      ticketId: order.ticket_instance_id,
                      course: courseIndex,
                    });
                    setIsFireCourseMode(false);
                    return;
                  }

                  if (isPrintCourseMode && onSelectPrintCourse) {
                    onSelectPrintCourse({
                      ticketId: order.ticket_instance_id,
                      orderId: order.order_id,
                      course: courseIndex,
                    });
                  }
                }}
              >
                {showCourseBumpDisplayBoxes && (!settings.use_course_separator || !hasSeparator) && (
                  renderCombinedCourseHeaderPanel(
                    courseIndex,
                    courseBoxes,
                    courseItems,
                    `course-header-panel-${order.ticket_instance_id}-${courseIndex}`
                  )
                )}
                {courseItems.map((item, index) => {
                  const isFirstInCourse = index === 0;

                  if (item.isCourseSeparator && settings.course_separator_top) return null;

                  const elements = [];

                  const hasTopSeparator =
                    isFirstInCourse &&
                    settings.use_course_separator &&
                    settings.course_separator_top;

                  const currentCategory = item.Category;
                  const willShowCategoryHeader =
                    settings.group_by_category && currentCategory !== lastCategory;

                  if (hasTopSeparator) {
                    elements.push(
                      <React.Fragment key={`sep-wrap-${courseIndex}`}>
                        {renderCourseSeparator(item)}
                        {renderCombinedCourseHeaderPanel(
                          courseIndex,
                          courseBoxes,
                          courseItems,
                          `course-header-panel-${order.ticket_instance_id}-${courseIndex}`
                        )}
                      </React.Fragment>
                    );
                  }

                  if (isFirstInCourse) {
                    if (!hasTopSeparator) {
                      if (!showCourseBumpDisplayBoxes) {
                        elements.push(
                          <React.Fragment key={`timing-no-sep-${courseIndex}`}>
                            {renderCombinedCourseHeaderPanel(
                              courseIndex,
                              courseBoxes,
                              courseItems,
                              `course-header-panel-${order.ticket_instance_id}-${courseIndex}`
                            )}
                          </React.Fragment>
                        );
                      }
                    }
                  }

                  if (willShowCategoryHeader) {
                    elements.push(
                      <div
                        key={`category-${courseIndex}-${index}`}
                        className={`font-bold uppercase text-purple-500 dark:text-purple-300 tracking-wider pt-2 ${uiScale.categoryHeader}`}
                      >
                        {currentCategory}
                      </div>
                    );
                  }

                  lastCategory = currentCategory;

                  if (item.isFireItem) {
                    const label =
                      Array.isArray(item.Condiment) && item.Condiment[0]?.Name
                        ? item.Condiment[0].Name.replace(/^--|--$/g, "")
                        : "UNKNOWN";

                    elements.push(
                      <React.Fragment key={`fire-${item.ID}`}>
                        <li className="text-sm text-red-600 font-semibold text-center border border-red-300 dark:border-red-500 rounded bg-red-50 dark:bg-red-900 py-1 px-2 tracking-wide uppercase">
                          FIRE {label}
                        </li>
                      </React.Fragment>
                    );

                    return <React.Fragment key={`wrap-${item.ID}`}>{elements}</React.Fragment>;
                  }

                  if (item.isCourseSeparator) {
                    elements.push(
                      <React.Fragment key={`sep-inline-${item.ID}`}>
                        {renderCourseSeparator(item)}
                        {renderCombinedCourseHeaderPanel(
                          courseIndex,
                          courseBoxes,
                          courseItems,
                          `course-header-panel-${order.ticket_instance_id}-${courseIndex}`
                        )}
                      </React.Fragment>
                    );
                    return <React.Fragment key={`wrap-${item.ID}`}>{elements}</React.Fragment>;
                  }

                  const isFireableItem =
                    !item.isFired &&
                    !item.isBumped &&
                    !item.isVoided &&
                    !item.isParked &&
                    !item.isCourseSeparator &&
                    !item.isMessage &&
                    !item.isFireItem &&
                    Number(item.TransType) !== 2 &&
                    String(item.status || "").toLowerCase() !== "voided" &&
                    String(item.status || "").toLowerCase() !== "bumped" &&
                    String(item.status || "").toLowerCase() !== "parked";

                  elements.push(
                    <div
                      key={`item-${item.ID}`}
                      className={
                        isFireItemMode && isFireableItem
                          ? "rounded-md border-4 border-yellow-400 bg-yellow-50/70 dark:bg-yellow-900/10 p-1"
                          : ""
                      }
                      onClick={(e) => {
                        if (!isFireItemMode || !isFireableItem) return;
                        e.stopPropagation();
                        onSelectFireItem({
                          ticketId: order.ticket_instance_id,
                          orderId: order.order_id,
                          itemId: item.ID,
                        });
                      }}
                    >
                      <TicketItem
                        ticketTouchStateRef={ticketTouchStateRef}
                        item={item}
                        ticketInstanceId={order.ticket_instance_id}
                        cookStartDelaySeconds={getCookStartDelaySeconds(item)}
                        cookTimingStartMs={getItemStartMs(item)}
                        onTap={(ticketInstanceId, itemId) =>
                          isFireItemMode && isFireableItem
                            ? onSelectFireItem({
                                ticketId: ticketInstanceId,
                                orderId: order.order_id,
                                itemId,
                              })
                            : undefined
                        }
                        onDoubleClick={(ticketInstanceId, itemId) => {
                          if (isFireItemMode && isFireableItem) return;
                          if (shouldDarkenPrepItem(item)) {
                            onPrepItemReady(ticketInstanceId, itemId);
                            return;
                          }
                          maybePromptHiddenPrepAfterItemBump(item);
                          onItemDoubleTap?.(ticketInstanceId, itemId);
                        }}
                        showCategory={settings.show_category}
                        settings={settings}
                        isPrepDisplayDarkened={shouldDarkenPrepItem(item)}
                        isBumpBarSelected={
                          isBumpBarItemMode &&
                          String(item.ID) === String(bumpBarSelectedItemId)
                        }
                      />
                    </div>
                  );

                  return <React.Fragment key={`wrap-${item.ID}`}>{elements}</React.Fragment>;
                })}
              </li>
            );
          });
        })()}
      </ul>

      <div className="relative flex-1">
        {!disableModeBlur && isFireCourseMode && (
          <div className="absolute inset-0 rounded backdrop-blur-sm z-0" aria-hidden="true" />
        )}

        <div className="m-1">
          {!settings.coursing ? (
            <button
              onClick={(event) => {
                logHeaderInput(event, "invoke_ticket_bump", { input_surface: "bump_button" });
                onBump?.(order);
              }}
              className="w-full bg-green-600 text-white text-xs px-3 py-1 rounded hover:bg-green-700"
            >
              Bump Ticket
            </button>
          ) : (
            <button
              onClick={(event) => {
                logHeaderInput(event, "invoke_next_course_bump", { input_surface: "bump_course_button" });
                handleBumpNextCourse();
              }}
              className={`w-full bg-blue-600 text-white ${uiScale.bumpbutton} px-3 py-1 rounded hover:bg-blue-700`}
            >
              Bump Course
            </button>
          )}
        </div>
      </div>

      {!disableBottomCutoffFade && showBottomCutoffFade && (
        <div
          className="fixed z-[220] pointer-events-none"
          style={{
            left: `${bottomCutoffFadeStyle?.left ?? 0}px`,
            width: `${bottomCutoffFadeStyle?.width ?? 0}px`,
            bottom: "3.5rem",
          }}
        >
          <div
            className="h-8 w-full"
            style={{
              background:
                "linear-gradient(to bottom, rgba(251,191,36,0) 0%, rgba(251,191,36,0.22) 48%, rgba(250,204,21,0.96) 100%)",
            }}
            aria-hidden="true"
          />
        </div>
      )}

      {hiddenPrepCoursePrompt && (
        <div
          className="fixed inset-0 z-[240] flex items-center justify-center bg-black/60 px-4"
          onClick={() => setHiddenPrepCoursePrompt(null)}
        >
          <div
            className="w-full max-w-md rounded-2xl bg-white p-5 text-gray-900 shadow-2xl dark:bg-gray-800 dark:text-gray-100"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-bold">Items Are Waiting To Cook</h3>
            <p className="mt-3 text-sm leading-relaxed text-gray-600 dark:text-gray-300">
              Some items in this course are hidden until it is time to start cooking them. Would you like to show them now?
            </p>
            <div className="mt-5 grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={handleBumpDespiteHiddenPrepItems}
                className="min-h-[52px] rounded-xl bg-gray-200 px-4 py-3 font-semibold text-gray-900 hover:bg-gray-300 active:scale-[0.98] dark:bg-gray-700 dark:text-gray-100 dark:hover:bg-gray-600"
              >
                No, Bump It
              </button>
              <button
                type="button"
                onClick={handleShowHiddenPrepItems}
                className="min-h-[52px] rounded-xl bg-blue-600 px-4 py-3 font-semibold text-white hover:bg-blue-700 active:scale-[0.98]"
              >
                Yes, Show Them
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

